/**
 * The download control on the Reports page.
 *
 * ONE BUTTON, NOT ONE PER BLOCK. A report tab draws a month chart and one or two
 * ranked breakdowns, and each of those is a different table with different
 * columns - so each is its own file. Putting a small download control on every
 * block would be four controls on the leads tab, three on most of the others,
 * and thirteen across a page whose whole job is to be read. They collapse into
 * one menu that lists what the OPEN tab can hand over.
 *
 * WHAT THE MENU SAYS OUT LOUD. Three of the thirteen tables are a top-N inside
 * the collector - eight clients, eight vendors, ten owners. The row says so
 * before the press rather than after, because a file named "top clients" holding
 * eight rows is indistinguishable from a desk with eight clients, and the
 * difference matters to anybody doing arithmetic with it.
 *
 * The window is whatever the range bar is showing. It is passed in rather than
 * read here so this component cannot drift from the numbers on screen.
 */
import { useState } from "react";
import { ChevronDown, Download, Loader2 } from "lucide-react";
import { cn } from "@wa/ui";
import { useT } from "../../../i18n/I18nProvider";
import {
  downloadReportCsv,
  REPORT_CSV_TABLES,
  type ReportKind,
} from "../../../lib/reports";
import { useToast } from "../../ui";
import { Pop, useClosePops } from "../../shell/Pop";
import type { Range } from "./RangeBar";

export function ExportMenu({ report, range }: { report: ReportKind; range: Range }) {
  const t = useT();
  const toast = useToast();
  const close = useClosePops();
  /**
   * The table currently downloading, not a plain boolean.
   *
   * The menu closes on the press, so a bare "busy" flag would only be visible
   * on the trigger and would say nothing about which of four tables was being
   * fetched. Keeping the key lets the trigger name it, and blocks a second
   * press of the SAME row without blocking a different one.
   */
  const [busy, setBusy] = useState<string | null>(null);

  const tables = REPORT_CSV_TABLES[report] ?? [];

  async function run(table: string) {
    close();
    if (busy) return;
    setBusy(table);
    try {
      await downloadReportCsv(report, table, range);
    } catch {
      // A refused or failed download leaves the page exactly as it was, so the
      // toast is the only thing that can say it did not happen.
      toast(t("common.error"));
    } finally {
      setBusy(null);
    }
  }

  return (
    <Pop
      trigger={(open) => (
        <button
          type="button"
          // The range bar's own pill vocabulary, not a `crm-btn`: this sits in
          // the same toolbar as the two date fields and has to read as one of
          // them rather than as the page's primary action.
          className={cn(
            "crm-fld crm-fld-sel w-auto min-w-0 gap-2.5 px-4 text-[12.75px]",
            open && "is-on",
          )}
          aria-haspopup="menu"
          aria-expanded={open}
        >
          {busy ? <Loader2 className="size-4 animate-spin" /> : <Download className="size-4" />}
          <span>{t("reports.csv.button")}</span>
          <ChevronDown className={cn("size-4 transition-transform", open && "rotate-180")} />
        </button>
      )}
    >
      {tables.map((tbl) => (
        <button
          key={tbl.table}
          type="button"
          className="crm-mi"
          onClick={() => void run(tbl.table)}
        >
          <span className="min-w-0 flex-1 truncate">{t(`reports.csv.${tbl.labelKey}`)}</span>
          {tbl.capped ? (
            <span className="shrink-0 text-xs text-muted-foreground">
              {t("reports.csv.topN", { n: String(tbl.capped) })}
            </span>
          ) : null}
        </button>
      ))}
    </Pop>
  );
}
